import {
  ResponseResult,
  type QuestionWithResponse,
} from '@amzn/gbsa-helios-typescript-client';
import type { MandatoryFailViolation } from '../../../shared/domain';

const QUESTION_ID_PARAM = 'questionId';
const SUB_QUESTION_ID_PARAM = 'subQuestionId';
const NAV_TARGET_ID_PREFIX = 'audit-question-nav-target';

export interface AuditQuestionTarget {
  readonly questionId: string;
  readonly subQuestionId?: string;
}

export interface QuestionNavRevealRequest {
  readonly requestId: number;
  readonly target: AuditQuestionTarget;
}

export interface AuditClosureNotice {
  readonly unansweredCount: number;
  readonly issueCount: number;
  readonly firstTarget: AuditQuestionTarget | undefined;
}

export function readAuditQuestionTarget(
  searchParams: URLSearchParams,
): AuditQuestionTarget | undefined {
  const questionId = searchParams.get(QUESTION_ID_PARAM);
  if (!questionId) return undefined;
  const subQuestionId = searchParams.get(SUB_QUESTION_ID_PARAM) ?? undefined;
  return subQuestionId ? { questionId, subQuestionId } : { questionId };
}

export function writeAuditQuestionTarget(
  searchParams: URLSearchParams,
  target: AuditQuestionTarget,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.set(QUESTION_ID_PARAM, target.questionId);
  if (target.subQuestionId) next.set(SUB_QUESTION_ID_PARAM, target.subQuestionId);
  else next.delete(SUB_QUESTION_ID_PARAM);
  return next;
}

export function getAuditQuestionNavTargetId(target: AuditQuestionTarget): string {
  const rawTarget = target.subQuestionId
    ? `${target.questionId}-${target.subQuestionId}`
    : target.questionId;
  return `${NAV_TARGET_ID_PREFIX}-${encodeURIComponent(rawTarget)}`;
}

export function deriveAuditClosureNotice(
  questions: readonly QuestionWithResponse[],
  violations: ReadonlyMap<string, readonly MandatoryFailViolation[]>,
): AuditClosureNotice {
  let unansweredCount = 0;
  let firstUnansweredTarget: AuditQuestionTarget | undefined;
  let firstViolationTarget: AuditQuestionTarget | undefined;

  for (const question of questions) {
    const questionId = question.questionDetails?.questionId;
    if (!questionId) continue;
    const result = question.responseDetails?.mainQuestionResponse?.responseResult;
    if (!isAnswered(result)) {
      unansweredCount++;
      firstUnansweredTarget ??= { questionId };
    }
    if (!firstViolationTarget) {
      const firstViolation = violations.get(questionId)?.at(0);
      if (firstViolation) {
        firstViolationTarget = firstViolation.subQuestionId
          ? { questionId, subQuestionId: firstViolation.subQuestionId }
          : { questionId };
      }
    }
  }

  return {
    unansweredCount,
    issueCount: violations.size,
    firstTarget: firstViolationTarget ?? firstUnansweredTarget,
  };
}

function isAnswered(result: string | undefined): boolean {
  return (
    result === ResponseResult.PASS ||
    result === ResponseResult.FAIL ||
    result === ResponseResult.NA
  );
}
